import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { X, ChevronDown, ChevronUp, Cookie } from "lucide-react";
import { Link } from "react-router-dom";

interface CookiePreferences {
  essential: boolean; // always true, read-only
  performance: boolean;
  functional: boolean;
}

const DEFAULT_PREFS: CookiePreferences = {
  essential: true,
  performance: false,
  functional: false,
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFS);

  useEffect(() => {
    const cookieChoice = localStorage.getItem("cookieConsent");
    if (cookieChoice === null) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (value: string, selectedPrefs: CookiePreferences) => {
    localStorage.setItem("cookieConsent", value);
    localStorage.setItem("cookiePreferences", JSON.stringify(selectedPrefs));
    setIsVisible(false);
  };

  const handleAcceptAll = () =>
    save("accepted", { essential: true, performance: true, functional: true });

  const handleRejectNonEssential = () =>
    save("rejected", {
      essential: true,
      performance: false,
      functional: false,
    });

  const handleSavePreferences = () => save("custom", prefs);

  const toggle = (key: keyof Omit<CookiePreferences, "essential">) =>
    setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-2xl"
        >
          <div className="container mx-auto max-w-5xl px-4 py-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex items-center gap-2">
                <Cookie className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                <h3 className="font-semibold text-base text-gray-900 dark:text-gray-100">
                  We respect your privacy
                </h3>
              </div>
              <button
                onClick={() => setIsVisible(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
                aria-label="Dismiss cookie banner"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
              We use essential cookies to ensure the website functions properly.
              With your consent, we also use optional cookies to improve
              performance, analyze traffic, and enhance your experience. You can
              accept all cookies, reject non-essential cookies, or customize
              your preferences at any time.
            </p>

            {/* Customize panel */}
            <AnimatePresence>
              {showCustomize && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="overflow-hidden mb-4"
                >
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
                    {/* Essential */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          Essential cookies
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Required for the site to function. Cannot be disabled.
                        </p>
                      </div>
                      <span className="text-xs font-semibold text-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                        Always on
                      </span>
                    </div>
                    {/* Performance */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          Performance & analytics
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Help us understand how visitors use the site.
                        </p>
                      </div>
                      <button
                        onClick={() => toggle("performance")}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          prefs.performance
                            ? "bg-emerald-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                        aria-checked={prefs.performance}
                        role="switch"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            prefs.performance
                              ? "translate-x-6"
                              : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                    {/* Functional */}
                    <div className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                          Functional cookies
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Enable enhanced features and personalisation.
                        </p>
                      </div>
                      <button
                        onClick={() => toggle("functional")}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                          prefs.functional
                            ? "bg-emerald-500"
                            : "bg-gray-300 dark:bg-gray-600"
                        }`}
                        aria-checked={prefs.functional}
                        role="switch"
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            prefs.functional ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Buttons + footer */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <Button
                onClick={handleAcceptAll}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Accept All
              </Button>
              <Button variant="outline" onClick={handleRejectNonEssential}>
                Reject Non-Essential
              </Button>
              {showCustomize ? (
                <Button
                  variant="outline"
                  onClick={handleSavePreferences}
                  className="sm:ml-auto"
                >
                  Save Preferences
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => setShowCustomize(true)}
                  className="sm:ml-auto flex items-center gap-1"
                >
                  Customize Preferences
                  <ChevronDown className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Footer links */}
            <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
              <Link
                to="/terms-and-privacy#cookies"
                className="underline hover:text-gray-600 dark:hover:text-gray-300"
              >
                Cookie Policy
              </Link>
              {" | "}
              <Link
                to="/terms-and-privacy#privacy"
                className="underline hover:text-gray-600 dark:hover:text-gray-300"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
